import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { KpiCard, DashboardHeader, SectionTable } from "../components/DashboardWidgets";
import { Wrench, ListChecks, CheckCircle, ClockClockwise, UserCircle, Warning } from "@phosphor-icons/react";

const KPI = [
  { key: "total_orders",                label: "Total Orders",          icon: ListChecks,     tone: "slate" },
  { key: "installation_pending",        label: "Pending Installation",  icon: ClockClockwise, tone: "amber" },
  { key: "installation_in_progress",    label: "In Progress",           icon: Wrench,         tone: "blue" },
  { key: "installation_completed",      label: "Completed",             icon: CheckCircle,    tone: "emerald" },
  { key: "open_service_tasks",          label: "Open Service Tasks",    icon: ListChecks,     tone: "amber" },
  { key: "completed_service_tasks",     label: "Completed Tasks",       icon: CheckCircle,    tone: "emerald" },
  { key: "signoff_pending",             label: "Sign-off Pending",      icon: Warning,        tone: "rose" },
];

const ServiceDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/dashboard/service`); setData(r.data); }
    catch { toast.error("Failed to load Service dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <DashboardHeader overline="Service & Installation" title="Service Dashboard" subtitle="Installation pipeline, installer-wise jobs, sign-off pending." onRefresh={load} refreshTestId="service-refresh-btn" />
          {loading ? <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div> : (
            <>
              <div data-testid="service-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI.map(k => <KpiCard key={k.key} icon={k.icon} label={k.label} tone={k.tone} money={k.money} value={data?.kpis?.[k.key]} testId={`service-kpi-${k.key}`} />)}
              </div>
              <SectionTable
                title="Installer Workload" icon={UserCircle} testId="service-by-installer"
                columns={[{ label: "Installer" }, { label: "Pending", align: "right" }, { label: "In Progress", align: "right" }, { label: "Completed", align: "right" }]}
                rows={data?.by_installer || []}
                emptyText="No installation jobs assigned yet."
                renderRow={(r, i) => (
                  <tr key={r.installer + i} data-testid={`service-installer-row-${i}`} className="border-b border-slate-100">
                    <td className="p-3 font-medium">{r.installer}</td>
                    <td className="p-3 text-right tabular-nums text-amber-700">{r.pending}</td>
                    <td className="p-3 text-right tabular-nums text-blue-700">{r.in_progress}</td>
                    <td className="p-3 text-right tabular-nums text-emerald-700">{r.completed}</td>
                  </tr>
                )}
              />
              <SectionTable
                title="Pending Installations" icon={ClockClockwise} testId="service-pending"
                columns={[{ label: "SO #" }, { label: "Customer" }, { label: "Installer" }, { label: "Status" }]}
                rows={data?.pending_installations || []}
                emptyText="Nothing pending."
                renderRow={(r, i) => (
                  <tr key={r.so_number + i} data-testid={`service-pending-row-${i}`} className="border-b border-slate-100">
                    <td className="p-3 font-mono text-xs">{r.so_number || "—"}</td>
                    <td className="p-3">{r.customer_name}</td>
                    <td className="p-3 text-sm text-slate-700">{r.installer_name || <span className="text-slate-400">Unassigned</span>}</td>
                    <td className="p-3 text-xs uppercase tracking-wider">{r.status}</td>
                  </tr>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServiceDashboard;
